import { useFavorites } from "@/hooks";
import { IPanelScope, ITreeItem } from "@/interfaces";
import { FavoriteItem } from "@/types";
import { createContext, useContext } from "react";

export interface IFavoriteStore {
  favorites: FavoriteItem[];
  tree: ITreeItem[];
  maxNumberOfFavorites: number;
  activeFavorite: string | null;
  setActiveFavorite: (id: string | null) => void;
  handleActiveFavoriteChanged: (
    id: string | null,
    skipActiveFavorite?: boolean,
  ) => void;
  toggleFavorite: (favorite: ITreeItem | FavoriteItem) => Promise<void>;
  reloadFavorites: () => Promise<void>;
  saveFavorites: (data: FavoriteItem[]) => Promise<void>;
  renameFavorite: (fav: { id: string; name: string }) => void;
  topLevelCount: (favs: FavoriteItem[]) => number;
}

const initStoreValue = {
  maxNumberOfFavorites: 15,
  tree: [],
  favorites: [],
  activeFavorite: null,
  isLoading: false,
  error: "",
  handleActiveFavoriteChanged: () => {},
  toggleFavorite: async () => {},
  reloadFavorites: async () => {},
  saveFavorites: async () => {},
  renameFavorite: () => {},
  setActiveFavorite: () => {},
  topLevelCount: () => 0,
};

const FavoritesContext = createContext<IFavoriteStore>(initStoreValue);

interface FavoritesProviderProp {
  children: React.ReactNode;
  scope: IPanelScope;
  tree: ITreeItem[];
  maxNumberOfFavorites: number;
}
export const FavoritesProvider = ({
  children,
  scope,
  tree,
  maxNumberOfFavorites,
}: FavoritesProviderProp) => {
  const {
    favorites,
    toggleFavorite,
    reloadFavorites,
    saveFavorites,
    renameFavorite,
    activeFavorite,
    setActiveFavorite,
    handleActiveFavoriteChanged,
    topLevelCount,
  } = useFavorites(scope, tree, maxNumberOfFavorites);

  return (
    <FavoritesContext.Provider
      value={{
        tree,
        favorites,
        toggleFavorite,
        reloadFavorites,
        saveFavorites,
        renameFavorite,
        maxNumberOfFavorites,
        activeFavorite,
        setActiveFavorite,
        handleActiveFavoriteChanged,
        topLevelCount,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavoritesContext = () => {
  return useContext(FavoritesContext);
};
